import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Modal } from 'components/Modal';
import { ChildrenProp } from 'types/commons';

interface ModalContextInterface {
    showCloseButton?: boolean;
    showModal: (modalId: string, component: React.ReactNode, className?: string, showCloseButton?: boolean) => void;
    closeModal: () => void;
    modalOpened: boolean;
    modalData: ModalDataInterface | undefined;
    modalIsClosing: boolean;
}

export interface ModalDataInterface {
    component: React.ReactNode | undefined;
    id: string | undefined;
    className?: string;
    showCloseButton?: boolean;
}

const ModalContext = createContext({} as ModalContextInterface);

function ModalProviderData(): ModalContextInterface {
    const location = useLocation();
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const previousPathnameRef = useRef(location.pathname);

    const [modalData, setModalData] = useState<ModalDataInterface>();
    const [modalIsClosing, setModalIsClosing] = useState<boolean>(false);

    const showModal = useCallback((modalId: string, component: React.ReactNode, className?: string, showCloseButton?: boolean) => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }

        setModalIsClosing(false);
        setModalData({
            id: modalId,
            component,
            className,
            showCloseButton
        });
    }, []);

    const closeModal = useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }

        setModalIsClosing(true);

        closeTimeoutRef.current = setTimeout(() => {
            setModalData({
                component: undefined,
                id: undefined
            });
            setModalIsClosing(false);
            closeTimeoutRef.current = null;
        }, 300);
    }, []);

    useEffect(() => {
        if (previousPathnameRef.current === location.pathname) return;

        previousPathnameRef.current = location.pathname;

        if (modalData?.id !== 'notification') {
            closeModal();
        }
    }, [location.pathname, modalData?.id, closeModal]);

    const modalOpened = useMemo(() => {
        return typeof modalData?.id === 'string' && !!modalData?.id.length;
    }, [modalData?.id]);

    useEffect(() => {
        if (modalOpened) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
    }, [modalOpened]);

    const escFunction = useCallback((event: { key: string }) => {
        if (event.key === 'Escape') {
            closeModal();
        }
    }, []);

    useEffect(() => {
        const listener = async ({ data }: any) => {
            if (data.type === 'achievement') {
                setModalData({
                    component: '',
                    id: 'achievement'
                });
            }
        };

        document.addEventListener('keydown', escFunction, false);
        window.addEventListener('message', listener);

        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
            }

            window.removeEventListener('message', listener);
            document.removeEventListener('keydown', escFunction, false);
        };
    }, [escFunction]);

    return {
        showModal,
        closeModal,
        modalOpened,
        modalData,
        modalIsClosing
    };
}

const ModalProvider = ({ children }: ChildrenProp) => {
    const contextData: ModalContextInterface = ModalProviderData();
    return (
        <ModalContext.Provider value={contextData}>
            {children}
            {contextData.modalOpened && <Modal />}
        </ModalContext.Provider>
    );
};

const useModal = (): ModalContextInterface => {
    const context = useContext(ModalContext);

    if (typeof context === 'undefined') {
        throw new Error('useModal must be used within an ModalProvider');
    }

    return context;
};

export { ModalProvider, useModal };
